import React, { createContext, useContext, useState, useCallback } from 'react';

const VersionUpdateContext = createContext(null);

/**
 * Version Update Context Provider.
 * Manages the state for version update notifications.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children App tree.
 * @returns {JSX.Element} Provider.
 */
export const VersionUpdateProvider = ({ children }) => {
  const [hasNewVersion, setHasNewVersion] = useState(false);

  /**
   * Marks that a newer frontend build is available.
   *
   * @returns {void}
   */
  const notifyNewVersion = useCallback(() => {
    setHasNewVersion(true);
  }, []);

  /**
   * Hides the version-update banner without refreshing.
   *
   * @returns {void}
   */
  const dismissNotification = useCallback(() => {
    setHasNewVersion(false);
  }, []);

  /**
   * Reloads the page to pick up the new frontend build.
   *
   * @returns {void}
   */
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <VersionUpdateContext.Provider
      value={{
        hasNewVersion,
        notifyNewVersion,
        dismissNotification,
        refreshPage,
      }}
    >
      {children}
    </VersionUpdateContext.Provider>
  );
};

/**
 * Hook to access version update context.
 *
 * @returns {{hasNewVersion: boolean, notifyNewVersion: Function, dismissNotification: Function, refreshPage: Function}} Version update API.
 */
export const useVersionUpdate = () => {
  const context = useContext(VersionUpdateContext);
  if (!context) {
    throw new Error('useVersionUpdate must be used within VersionUpdateProvider');
  }
  return context;
};

export default VersionUpdateContext;
